import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, TrendingUp, Award, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Evolução Literária (AKs)
 * Displays and manages Author Keys (creative points)
 */
const EvolucaoLiteraria = ({ compact = false, showHistory = false }) => {
  const [aks, setAks] = useState({ total_aks: 0, recent_history: [] });
  const [loading, setLoading] = useState(true);
  const [showReward, setShowReward] = useState(false);
  const [lastReward, setLastReward] = useState(0);

  useEffect(() => {
    loadAks();
  }, []);

  const loadAks = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/creative/aks`, {
        withCredentials: true
      });
      setAks(response.data);
    } catch (error) {
      console.error('Error loading AKs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to award AKs (can be called from parent)
  const awardAks = async (action, metadata = {}) => {
    try {
      const response = await axios.post(`${API_URL}/api/creative/aks/award`, {
        action,
        metadata
      }, {
        withCredentials: true
      });

      if (response.data.success && response.data.awarded > 0) {
        setLastReward(response.data.awarded);
        setShowReward(true);
        setAks(prev => ({
          ...prev,
          total_aks: response.data.new_total
        }));
        
        // Hide reward animation after 2 seconds
        setTimeout(() => setShowReward(false), 2000);
        
        toast.success(`+${response.data.awarded} AKs!`);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error awarding AKs:', error);
      return { success: false };
    }
  };

  // Expose awardAks to window for global access
  useEffect(() => {
    window.awardAks = awardAks;
    return () => {
      delete window.awardAks;
    };
  }, []);

  if (loading) {
    return <div className={`animate-pulse ${compact ? 'h-10' : 'h-24'} bg-muted rounded-lg`} />;
  }

  if (compact) {
    return (
      <div className="relative flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border border-amber-500/20">
        <Star size={16} className="text-amber-500 fill-amber-500" />
        <span className="font-bold text-amber-600 dark:text-amber-400">
          {aks.total_aks.toLocaleString()}
        </span>
        <span className="text-xs text-muted-foreground">AKs</span>
        
        {/* Reward Animation */}
        {showReward && (
          <div className="absolute -top-6 right-0 animate-bounce text-green-500 font-bold text-sm">
            +{lastReward}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-5 rounded-xl border border-amber-200/50 dark:border-amber-800/30 bg-gradient-to-br from-amber-50/50 to-yellow-50/50 dark:from-amber-950/20 dark:to-yellow-950/20 relative overflow-hidden">
      {/* Reward Animation Overlay */}
      {showReward && (
        <div className="absolute inset-0 flex items-center justify-center bg-amber-500/10 animate-pulse">
          <div className="text-4xl font-bold text-amber-500 animate-bounce">
            +{lastReward} AKs!
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-lg shadow-amber-500/20">
            <Star size={20} className="text-white fill-white" />
          </div>
          <div>
            <h3 className="font-semibold">Evolução Literária</h3>
            <p className="text-xs text-muted-foreground">Seu capital criativo</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">
            {aks.total_aks.toLocaleString()}
          </p>
          <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
            <Sparkles size={10} className="text-amber-500" />
            Author Keys
          </p>
        </div>
      </div>

      {/* How to earn */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-2 p-2 rounded bg-white/50 dark:bg-gray-800/50">
          <TrendingUp size={12} className="text-amber-500" />
          <span>Escrever +10 AKs</span>
        </div>
        <div className="flex items-center gap-2 p-2 rounded bg-white/50 dark:bg-gray-800/50">
          <Award size={12} className="text-amber-500" />
          <span>DNA +20 AKs</span>
        </div>
      </div>

      {/* Recent History */}
      {showHistory && aks.recent_history && aks.recent_history.length > 0 && (
        <div className="mt-4 pt-4 border-t border-amber-200/30 dark:border-amber-800/30">
          <p className="text-xs font-medium text-muted-foreground mb-2">Últimas conquistas</p>
          <div className="space-y-1">
            {aks.recent_history.slice(-5).map((entry, index) => (
              <div key={index} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{entry.action}</span>
                <span className="text-green-500 font-medium">+{entry.aks}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EvolucaoLiteraria;
